import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { signIn, useAuth } from '@/hooks/useAuth'
import { useTheme } from '@/contexts/ThemeContext'
import {Sun, Moon, Loader2, Eye, EyeOff, ArrowLeft, ShieldCheck} from 'lucide-react'
import toast from 'react-hot-toast'

/* ── Paleta SISMEDI-inspired — azul institucional ── */
const GREEN      = '#1A56DB'
const GREEN_LIGHT= '#3B72F0'
const GREEN_BG   = '#EFF6FF'
const ORANGE     = '#E8710A'

export default function AdminLogin() {
  const [username, setUsername]         = useState('')
  const [password, setPassword]         = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading]           = useState(false)

  const { theme, toggleTheme }         = useTheme()
  const { isAuthenticated, isLoading } = useAuth()
  const navigate                       = useNavigate()
  const location                       = useLocation()
  const from                           = (location.state as any)?.from || '/admin'

  useEffect(() => {
    if (!isLoading && isAuthenticated) navigate(from, { replace: true })
  }, [isAuthenticated, isLoading, navigate, from])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <Loader2 size={28} className="animate-spin" style={{ color: GREEN }} />
      </div>
    )
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!username.trim()) { toast.error('Digite o nome de utilizador.'); return }
    if (!password)        { toast.error('Digite a senha.'); return }
    setLoading(true)
    try {
      await signIn(username, password)
      toast.success('Login realizado com sucesso!')
      navigate(from, { replace: true })
    } catch (err: any) {
      toast.error(err?.message ?? 'Utilizador ou senha incorretos.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8 transition-colors"
      style={{ background: GREEN_BG }}
    >
      {/* Tema */}
      <button
        onClick={toggleTheme}
        className="fixed top-4 right-4 p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-white/60 dark:hover:bg-gray-800 transition-all"
        aria-label="Alternar tema"
      >
        {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
      </button>

      {/* Voltar */}
      <a
        href="/"
        className="fixed top-4 left-4 flex items-center gap-1.5 text-sm font-medium transition-colors"
        style={{ color: GREEN }}
        onMouseEnter={e => (e.currentTarget.style.color = ORANGE)}
        onMouseLeave={e => (e.currentTarget.style.color = GREEN)}
      >
        <ArrowLeft size={16} />
        Voltar ao site
      </a>

      <div className="w-full max-w-sm">
        {/* Card */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border overflow-hidden" style={{ borderColor: '#bfdbfe' }}>

          {/* Barra superior azul */}
          <div className="h-1.5 w-full" style={{ background: GREEN }} />

          <div className="p-8">

            {/* Ícone + Título */}
            <div className="flex flex-col items-center mb-8">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 shadow-sm"
                style={{ background: '#F0F7FF' }}
              >
                <ShieldCheck size={26} style={{ color: GREEN }} />
              </div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white text-center">
                Painel Administrativo
              </h1>
              <p className="text-sm mt-1 font-medium" style={{ color: GREEN }}>
                PALACIO DA SABEDORIA
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5" noValidate>
              {/* Utilizador */}
              <div>
                <label
                  htmlFor="login-username"
                  className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5"
                >
                  Utilizador <span style={{ color: ORANGE }}>*</span>
                </label>
                <input
                  id="login-username"
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="Ex: Admin"
                  required
                  autoComplete="username"
                  autoFocus
                  spellCheck={false}
                  className="w-full px-3.5 py-2.5 rounded-xl border bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm transition-all placeholder-gray-400 focus:outline-none"
                  style={{ borderColor: '#bfdbfe' }}
                  onFocus={e => (e.currentTarget.style.boxShadow = `0 0 0 2px ${GREEN}40`)}
                  onBlur={e => (e.currentTarget.style.boxShadow = '')}
                />
              </div>

              {/* Senha */}
              <div>
                <label
                  htmlFor="login-password"
                  className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5"
                >
                  Senha <span style={{ color: ORANGE }}>*</span>
                </label>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                    className="w-full px-3.5 py-2.5 pr-11 rounded-xl border bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm transition-all placeholder-gray-400 focus:outline-none"
                    style={{ borderColor: '#bfdbfe' }}
                    onFocus={e => (e.currentTarget.style.boxShadow = `0 0 0 2px ${GREEN}40`)}
                    onBlur={e => (e.currentTarget.style.boxShadow = '')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Botão entrar */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 text-white font-bold rounded-xl text-sm transition-all flex items-center justify-center gap-2 focus:outline-none disabled:opacity-60 shadow-sm"
                style={{ background: loading ? '#6a9af0' : GREEN }}
                onMouseEnter={e => { if (!loading) e.currentTarget.style.background = GREEN_LIGHT }}
                onMouseLeave={e => { if (!loading) e.currentTarget.style.background = GREEN }}
              >
                {loading
                  ? <><Loader2 size={16} className="animate-spin" />A entrar...</>
                  : 'Entrar'
                }
              </button>
            </form>

            <p className="mt-6 text-xs text-gray-400 text-center">
              Área restrita — acesso apenas a administradores.
            </p>
          </div>
        </div>

        {/* Rodapé do card */}
        <p className="text-center text-xs mt-5" style={{ color: '#5b7abf' }}>
          © {new Date().getFullYear()} PALACIO DA SABEDORIA
        </p>
      </div>
    </div>
  )
}
